import { useEffect } from "react";

/**
 * Adds `<meta name="robots" content="noindex, nofollow">` to the document
 * head while the calling component is mounted, and removes it on unmount.
 *
 * Use on private / auth / dashboard pages so search engines don't index
 * them even if they reach the URL via a stray backlink. Complements
 * (does NOT replace) the Disallow rules in /public/robots.txt.
 *
 * Note: SPAs share a single <head> — this hook temporarily overrides the
 * baseline `<meta name="robots" content="index, follow">` shipped in
 * public/index.html. The baseline is automatically restored via the
 * cleanup below whenever the user navigates away.
 */
export function useNoIndex() {
  useEffect(() => {
    // If there's already a robots meta tag (from index.html), remember its
    // original value so we can restore it on unmount — otherwise the entire
    // rest of the SPA would stay noindex until a full page reload.
    const existing = document.querySelector('meta[name="robots"]');
    const previous = existing ? existing.getAttribute("content") : null;

    if (existing) {
      existing.setAttribute("content", "noindex, nofollow");
    } else {
      const tag = document.createElement("meta");
      tag.name = "robots";
      tag.content = "noindex, nofollow";
      tag.setAttribute("data-psg-noindex", "true");
      document.head.appendChild(tag);
    }

    return () => {
      const current = document.querySelector('meta[name="robots"]');
      if (!current) return;
      if (current.getAttribute("data-psg-noindex") === "true") {
        // We created this tag — remove it entirely.
        current.remove();
      } else if (previous !== null) {
        // We modified the pre-existing baseline — restore it.
        current.setAttribute("content", previous);
      }
    };
  }, []);
}
